import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Button, Stack, Chip, Avatar, IconButton, Tooltip, InputBase, Snackbar, Alert, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import MarkEmailUnreadRoundedIcon from '@mui/icons-material/MarkEmailUnreadRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DraftsRoundedIcon from '@mui/icons-material/DraftsRounded';
import MailRoundedIcon from '@mui/icons-material/MailRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import MoreTimeRoundedIcon from '@mui/icons-material/MoreTimeRounded';
import BeachAccessRoundedIcon from '@mui/icons-material/BeachAccessRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import CakeRoundedIcon from '@mui/icons-material/CakeRounded';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setUnreadCount } from '../redux/slices/inboxSlice';
import { PRIMARY, PRIMARY_LIGHT } from '../theme';
import { initialsFromName as initials, avatarTone } from '../utils/format';
import { solidBtn, ghostBtn, Panel, EmptyState, ConfirmDialog, LoadAlert } from '../components/uiKit';
import { Segmented } from '../components/LeaveAttendanceComps/punchUi';
import http, { apiErrorMessage } from '../Api/http';
import { SkeletonRows } from '../components/uiKit';
import {
    GetInbox, UpdateMessageReadStatus, MarkAllRead, DeleteMessage, ClearAll,
    GetMyDocumentRequests, PostMyDocumentUpload,
} from '../Api/Api';

const statusPill = (color, bg, border) => ({
    alignItems: 'center', gap: '5px', height: 22, px: 1, mr: 0.3,
    borderRadius: '999px', border: `1px solid ${border}`, bgcolor: bg, color,
    fontSize: 10.5, fontWeight: 800, whiteSpace: 'nowrap', lineHeight: 1,
});
const dotSx = (c) => ({ width: 6, height: 6, borderRadius: '50%', bgcolor: c, flexShrink: 0 });
const iconBtnSx = { width: 32, height: 32, borderRadius: '8px', border: '1px solid #E6EAF1', bgcolor: '#fff' };

const KINDS = {
    'document-request': { icon: AssignmentRoundedIcon, color: PRIMARY, bg: PRIMARY_LIGHT, label: 'Document request' },
    'document-submitted': { icon: UploadFileRoundedIcon, color: '#0369A1', bg: '#E0F2FE', label: 'Submission' },
    'document-approved': { icon: CheckCircleRoundedIcon, color: '#16A34A', bg: '#DCFCE7', label: 'Approved' },
    'document-rejected': { icon: CancelRoundedIcon, color: '#E11D48', bg: '#FEE2E2', label: 'Rejected' },
    advance: { icon: SavingsRoundedIcon, color: '#B45309', bg: '#FFF7ED', label: 'Salary advance' },
    overtime: { icon: MoreTimeRoundedIcon, color: '#0891B2', bg: '#ECFEFF', label: 'Overtime' },
    leave: { icon: BeachAccessRoundedIcon, color: '#135F93', bg: '#EBF5FC', label: 'Leave' },
    'photo-reupload': { icon: PhotoCameraRoundedIcon, color: '#B45309', bg: '#FEF3C7', label: 'Photo re-upload' },
    birthday: { icon: CakeRoundedIcon, color: '#DB2777', bg: '#FCE7F3', label: 'Birthday' },
    announcement: { icon: CampaignRoundedIcon, color: '#64748B', bg: '#F1F5F9', label: 'Announcement' },
};
const kindOf = (k) => KINDS[k] || KINDS.announcement;

const inferKind = (type, category) => {
    const t = String(type || '').toLowerCase();
    const c = String(category || '').toLowerCase();
    // Photo attendance was retired server-side; historical messages can still be
    // in someone's inbox, so they keep their label — there is just no action left.
    if (c.includes('photo')) return 'photo-reupload';
    if (KINDS[t]) return t;
    if (t.includes('request')) return 'document-request';
    if (t.includes('submiss') || t.includes('submit') || t.includes('upload')) return 'document-submitted';
    if (t.includes('approv')) return 'document-approved';
    if (t.includes('reject') || t.includes('declin')) return 'document-rejected';
    if (t.includes('advance')) return 'advance';
    if (t.includes('overtime')) return 'overtime';
    if (t.includes('leave')) return 'leave';
    if (t.includes('birthday')) return 'birthday';
    if (t.includes('announce')) return 'announcement';
    return String(category || '').toLowerCase().includes('doc') ? 'document-request' : 'announcement';
};
const normalizeMessage = (m, i) => ({
    id: m.id ?? `msg-${i}`,
    kind: inferKind(m.type, m.category),
    title: m.title ?? '',
    body: m.message ?? '',
    from: m.actorName ?? 'System',
    initials: m.actorInitials ?? '',
    timeAgo: m.timeAgo ?? m.createdOn ?? '',
    needsAction: Boolean(m.needsAction),
    read: Boolean(m.isRead),
    refId: m.refId ?? null,
    actionPath: m.actionPath ?? null,
    isDocRequest: String(m.type || '').toLowerCase() === 'request' && String(m.category || '').toLowerCase().includes('doc'),
});

export default function InboxPage() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [items, setItems] = useState([]);
    const [docReqs, setDocReqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    const [q, setQ] = useState('');
    const [view, setView] = useState('all');
    const [confirm, setConfirm] = useState(false);
    const [snack, setSnack] = useState(null);

    const notify = (msg, sev = 'success') => setSnack({ msg, sev });
    const unread = useMemo(() => items.filter((m) => !m.read).length, [items]);

    useEffect(() => { dispatch(setUnreadCount(unread)); }, [unread, dispatch]);

    const loadInbox = useCallback(async () => {
        setLoading(true);
        const [inboxRes, docRes] = await Promise.allSettled([
            http.get(GetInbox, { params: { filter: 'all' } }),
            http.get(GetMyDocumentRequests),
        ]);
        try {
            if (inboxRes.status === 'rejected') throw inboxRes.reason;
            const body = inboxRes.value?.data;
            if (body?.error) throw new Error(body.message || 'Could not load your inbox.');
            const list = Array.isArray(body?.data?.messages) ? body.data.messages : [];
            setItems(list.map(normalizeMessage));
            setLoadError('');
        } catch (err) {
            setLoadError(apiErrorMessage(err, 'Could not load your inbox.'));
        }
        const docBody = docRes.status === 'fulfilled' ? docRes.value?.data : null;
        setDocReqs(docBody && !docBody.error && Array.isArray(docBody.data?.items) ? docBody.data.items : []);
        setLoading(false);
    }, []);

    useEffect(() => { loadInbox(); }, [loadInbox]);

    const setRead = async (id, read) => {
        setItems((list) => list.map((m) => (m.id === id ? { ...m, read } : m)));
        try {
            const { data: body } = await http.put(UpdateMessageReadStatus, { Id: id, IsRead: read });
            if (body?.error) throw new Error(body.message);
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not update the message.'), 'error');
            loadInbox();
        }
    };

    const markAllRead = async () => {
        setItems((list) => list.map((m) => ({ ...m, read: true })));
        try {
            const { data: body } = await http.put(MarkAllRead);
            if (body?.error) throw new Error(body.message);
            notify('All messages marked as read.');
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not mark all as read.'), 'error');
            loadInbox();
        }
    };

    const removeMessage = async (id) => {
        setItems((list) => list.filter((m) => m.id !== id));
        try {
            const { data: body } = await http.delete(DeleteMessage, { params: { id } });
            if (body?.error) throw new Error(body.message);
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not delete the message.'), 'error');
            loadInbox();
        }
    };

    const clearInbox = async () => {
        setItems([]);
        try {
            const { data: body } = await http.delete(ClearAll);
            if (body?.error) throw new Error(body.message);
            notify('Inbox cleared.');
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not clear the inbox.'), 'error');
            loadInbox();
        }
    };

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return items;
        return items.filter((m) => [m.title, m.body, m.from].some((v) => String(v || '').toLowerCase().includes(s)));
    }, [items, q]);

    const [openMsg, setOpenMsg] = useState(null);
    const openMessage = (m) => {
        if (!m.read) setRead(m.id, true);
        setOpenMsg(m);
    };
    const goTo = (m) => {
        setOpenMsg(null);
        if (m.actionPath) navigate(m.actionPath);
    };

    const [upload, setUpload] = useState(null);
    const fileRef = useRef(null);

    const openUpload = async (m) => {
        setUpload({ msg: m, requestId: null, docName: m.title, note: '', file: null, resolving: true, saving: false });
        try {
            const { data: body } = await http.get(GetMyDocumentRequests);
            const items = Array.isArray(body?.data?.items) ? body.data.items : [];
            const match = items.find((it) => String(it.recipientId) === String(m.refId))
                || items.find((it) => String(it.requestId) === String(m.refId));
            setUpload((u) => u && { ...u, requestId: match?.requestId ?? m.refId, docName: match?.documentName || m.title, note: match?.note || '', resolving: false });
        } catch {
            setUpload((u) => u && { ...u, requestId: m.refId, resolving: false });
        }
    };

    const submitUpload = async () => {
        if (!upload?.file) { notify('Choose a file to upload.', 'warning'); return; }
        if (upload.requestId == null) { notify('Could not find the matching request.', 'error'); return; }
        setUpload((u) => ({ ...u, saving: true }));
        try {
            const fd = new FormData();
            fd.append('RequestId', String(upload.requestId));
            fd.append('Note', upload.note || '');
            fd.append('File', upload.file);
            const { data: body } = await http.post(PostMyDocumentUpload, fd);
            if (body?.error) throw new Error(body.message || 'Upload failed.');
            notify(body?.message || 'Document uploaded — pending review.');
            setUpload(null);
            await loadInbox();
        } catch (err) {
            notify(apiErrorMessage(err, 'Upload failed.'), 'error');
            setUpload((u) => u && { ...u, saving: false });
        }
    };

    const docStatusFor = (refId) => {
        if (refId == null) return null;
        return docReqs.find((d) => String(d.recipientId) === String(refId))
            || docReqs.find((d) => String(d.requestId) === String(refId))
            || null;
    };

    const stillPending = (m) => {
        if (!m.needsAction) return false;
        if (m.isDocRequest) {
            const status = String(docStatusFor(m.refId)?.status || '').toLowerCase();
            return !['submitted', 'approved'].includes(status);
        }
        return true;
    };

    const counts = {
        total: items.length,
        unread,
        docs: items.filter((m) => String(m.kind || '').startsWith('document')).length,
        actionable: items.filter(stillPending).length,
    };

    // The old cards showed Unread and Read — two halves of the same total — and
    // none of the four numbers could be clicked. As segments they filter, and
    // the list starts at the top of the page instead of 500px down.
    const VIEWS = [
        { key: 'all', label: 'All', count: counts.total },
        { key: 'unread', label: 'Unread', count: counts.unread },
        { key: 'action', label: 'Needs action', count: counts.actionable },
        { key: 'docs', label: 'Documents', count: counts.docs },
    ];

    // The search narrows first, then the chosen view — so a count in the tab
    // always matches what the list shows for that tab.
    const visible = filtered.filter((m) => {
        if (view === 'unread') return !m.read;
        if (view === 'action') return stillPending(m);
        if (view === 'docs') return String(m.kind || '').startsWith('document');
        return true;
    });

    // Refresh, Mark all read and Clear all sit at the right end of the breadcrumb row.
    const { setCrumbActions } = useOutletContext() || {};
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        setCrumbActions(
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button onClick={loadInbox} disabled={loading} startIcon={<RefreshRoundedIcon />} sx={{ ...ghostBtn, height: 42, px: 2 }}>
                Refresh
            </Button>
            {unread > 0 && (
                <Button onClick={markAllRead} startIcon={<DoneAllRoundedIcon />} sx={{ ...ghostBtn, height: 42, px: 2 }}>
                    Mark all read
                </Button>
            )}
            {items.length > 0 && (
                <Button onClick={() => setConfirm(true)} startIcon={<DeleteOutlineRoundedIcon />} sx={{ ...ghostBtn, height: 42, px: 2, color: '#E11D48', borderColor: '#FBCFE8', '&:hover': { bgcolor: '#FEF2F2' } }}>
                    Clear all
                </Button>
            )}
            </Box>,
        );
        return () => setCrumbActions(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setCrumbActions, loading, unread, items.length, loadInbox]);

    return (
        <Box sx={{ px: { xs: 1.5, md: 2.5 }, pt: 2.5, pb: 3 }}>
            <Panel
                title="Messages"
                icon={MailRoundedIcon}
                action={(
                    <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1.2, justifyContent: 'flex-end' }}>
                        <Segmented items={VIEWS} value={view} onChange={setView} />
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '7px', px: 1.4, height: 38, width: { xs: 150, sm: 210 } }}>
                            <SearchRoundedIcon sx={{ fontSize: 18, color: '#98A0AE' }} />
                            <InputBase placeholder="Search messages…" value={q} onChange={(e) => setQ(e.target.value)} sx={{ fontSize: 13, flex: 1 }} />
                        </Stack>
                    </Stack>
                )}
            >
                {loading ? (
                    <SkeletonRows count={5} />
                ) : loadError ? (
                    <Box sx={{ p: 3 }}>
                        <LoadAlert sx={{ borderRadius: '9px' }}
                            action={<Button size="small" onClick={loadInbox} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>
                            {loadError}
                        </LoadAlert>
                    </Box>
                ) : visible.length === 0 ? (
                    <EmptyState
                        icon={InboxRoundedIcon}
                        title={q
                            ? 'Nothing matches that search'
                            : view === 'all' ? 'Your inbox is empty' : `Nothing under ${VIEWS.find((v) => v.key === view)?.label.toLowerCase()}`}
                        hint={q
                            ? 'Try another search term.'
                            : view === 'all'
                                ? 'Document requests, submissions and employee requests will show up here.'
                                : 'Switch back to All to see the rest of your messages.'}
                    />
                ) : (
                    <Box>
                        {visible.map((m) => {
                            const k = kindOf(m.kind);
                            const doc = m.isDocRequest ? docStatusFor(m.refId) : null;
                            const docStatus = String(doc?.status || '').toLowerCase();
                            const uploaded = ['submitted', 'approved'].includes(docStatus);
                            return (
                                <Stack
                                    key={m.id}
                                    direction="row"
                                    onClick={() => openMessage(m)}
                                    sx={{
                                        alignItems: 'flex-start', gap: 1.6, px: 2, py: 1.8, cursor: 'pointer',
                                        borderBottom: '1px solid #F3F8FC',
                                        bgcolor: m.read ? '#fff' : '#F3F8FC',
                                        borderLeft: m.read ? '3px solid transparent' : `3px solid ${k.color}`,
                                        transition: 'background-color .15s',
                                        '&:hover': { bgcolor: '#F3F9FD' },
                                        // A resting row is just content; the tools
                                        // arrive when the pointer does, so the right
                                        // edge isn't a permanent column of grey.
                                        '&:hover .inbox-tools': { opacity: 1, pointerEvents: 'auto' },
                                    }}
                                >
                                    <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: k.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.2 }}>
                                        <k.icon sx={{ fontSize: 20, color: k.color }} />
                                    </Box>

                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                                            <Typography sx={{ fontSize: 14, fontWeight: m.read ? 600 : 800, color: '#0F172A' }}>{m.title}</Typography>
                                            {!m.read && <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: k.color }} />}
                                            <Chip label={k.label} size="small" sx={{ height: 18, fontSize: 9.5, fontWeight: 700, bgcolor: k.bg, color: k.color }} />
                                        </Stack>
                                        <Typography sx={{ fontSize: 12.5, color: '#64748B', mt: 0.3, lineHeight: 1.5 }}>{m.body}</Typography>
                                        <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', mt: 0.7 }}>
                                            <Avatar sx={{ width: 20, height: 20, ...avatarTone(m.from), fontSize: 8.5, fontWeight: 800 }}>{m.initials || initials(m.from)}</Avatar>
                                            <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>{m.from}{m.timeAgo ? ` · ${m.timeAgo}` : ''}</Typography>
                                        </Stack>
                                    </Box>

                                    <Stack direction="row" spacing={0.6} sx={{ alignItems: 'center', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                                        {m.isDocRequest && !uploaded && (
                                            <Box sx={{ ...statusPill('#B45309', '#FFFBEB', '#FCD9A5'), display: { xs: 'none', sm: 'inline-flex' } }}>
                                                <Box sx={dotSx('#F59E0B')} />Action needed
                                            </Box>
                                        )}
                                        {m.isDocRequest && uploaded && (
                                            docStatus === 'approved' ? (
                                                <Box sx={{ ...statusPill('#16A34A', '#F0FDF4', '#BBF7D0'), display: { xs: 'none', sm: 'inline-flex' } }}>
                                                    <Box sx={dotSx('#22C55E')} />Approved
                                                </Box>
                                            ) : (
                                                <Box sx={{ ...statusPill('#0369A1', '#F0F9FF', '#BAE6FD'), display: { xs: 'none', sm: 'inline-flex' } }}>
                                                    <Box sx={dotSx('#0EA5E9')} />Submitted
                                                </Box>
                                            )
                                        )}
                                        <Stack
                                            className="inbox-tools"
                                            direction="row"
                                            spacing={0.6}
                                            sx={{
                                                alignItems: 'center',
                                                opacity: { xs: 1, md: 0 },
                                                pointerEvents: { xs: 'auto', md: 'none' },
                                                transition: 'opacity .15s',
                                            }}
                                        >
                                        <Tooltip arrow title="Open">
                                            <IconButton size="small" onClick={() => openMessage(m)} sx={{ ...iconBtnSx, color: '#94A3B8', transition: 'color .15s, background-color .15s', '&:hover': { color: PRIMARY, bgcolor: PRIMARY_LIGHT, borderColor: '#D1E8F8' } }}>
                                                <ArrowForwardRoundedIcon sx={{ fontSize: 17 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip arrow title={m.read ? 'Mark as unread' : 'Mark as read'}>
                                            <IconButton size="small" onClick={() => setRead(m.id, !m.read)} sx={{ ...iconBtnSx, color: m.read ? '#94A3B8' : PRIMARY, borderColor: m.read ? '#E6EAF1' : '#D1E8F8', bgcolor: m.read ? '#fff' : PRIMARY_LIGHT, '&:hover': { color: PRIMARY, bgcolor: PRIMARY_LIGHT, borderColor: '#D1E8F8' } }}>
                                                {m.read ? <DraftsRoundedIcon sx={{ fontSize: 18 }} /> : <MarkEmailUnreadRoundedIcon sx={{ fontSize: 18 }} />}
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip arrow title="Delete">
                                            <IconButton size="small" onClick={() => removeMessage(m.id)} sx={{ ...iconBtnSx, color: '#94A3B8', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2', borderColor: '#FBCFE8' } }}>
                                                <DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} />
                                            </IconButton>
                                        </Tooltip>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            );
                        })}
                    </Box>
                )}
            </Panel>

            <Dialog open={Boolean(openMsg)} onClose={() => setOpenMsg(null)} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '14px', overflow: 'hidden' } } }}>
                {openMsg && (() => {
                    const k = kindOf(openMsg.kind);
                    const doc = openMsg.isDocRequest ? docStatusFor(openMsg.refId) : null;
                    const dStatus = String(doc?.status || '').toLowerCase();
                    const dUploaded = ['submitted', 'approved'].includes(dStatus);
                    return (
                        <>
                            <Box sx={{ px: 3, pt: 2.4, pb: 2, background: `linear-gradient(135deg, ${k.bg} 0%, #fff 78%)`, borderBottom: '1px solid #EEF0F6' }}>
                                <Stack direction="row" spacing={1.4} sx={{ alignItems: 'flex-start' }}>
                                    <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: '#fff', border: `1px solid ${k.color}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <k.icon sx={{ fontSize: 22, color: k.color }} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Chip label={k.label} size="small" sx={{ height: 18, fontSize: 9.5, fontWeight: 800, bgcolor: k.bg, color: k.color, mb: 0.5 }} />
                                        <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#0F172A', lineHeight: 1.25 }}>{openMsg.title}</Typography>
                                    </Box>
                                    <IconButton onClick={() => setOpenMsg(null)} sx={{ color: '#94A3B8', mt: -0.5, mr: -0.5, '&:hover': { color: '#0F172A' } }}>
                                        <CloseRoundedIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                </Stack>
                            </Box>

                            <DialogContent sx={{ px: 3, py: 2.4 }}>
                                <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center', pb: 2, borderBottom: '1px solid #F3F8FC' }}>
                                    <Avatar sx={{ width: 38, height: 38, ...avatarTone(openMsg.from), fontSize: 13, fontWeight: 800 }}>{openMsg.initials || initials(openMsg.from)}</Avatar>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }}>{openMsg.from}</Typography>
                                        <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>to me{openMsg.timeAgo ? ` · ${openMsg.timeAgo}` : ''}</Typography>
                                    </Box>
                                </Stack>

                                <Typography sx={{ fontSize: 14, color: '#334155', lineHeight: 1.7, mt: 2, whiteSpace: 'pre-wrap' }}>
                                    {openMsg.body || 'No message body.'}
                                </Typography>

                                {openMsg.isDocRequest && dUploaded && (
                                    <Chip label={dStatus === 'approved' ? 'Approved · filed in your record' : 'Submitted · awaiting review'} size="small"
                                        sx={{ mt: 2.2, height: 26, fontSize: 11.5, fontWeight: 800, bgcolor: dStatus === 'approved' ? '#DCFCE7' : '#FEF3C7', color: dStatus === 'approved' ? '#16A34A' : '#B45309' }} />
                                )}
                            </DialogContent>

                            <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1, flexWrap: 'wrap' }}>
                                <Button onClick={() => { const id = openMsg.id; setOpenMsg(null); removeMessage(id); }}
                                    startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} />}
                                    sx={{ ...ghostBtn, height: 40, px: 1.8, color: '#E11D48', borderColor: '#FBCFE8', '&:hover': { bgcolor: '#FEF2F2' } }}>
                                    Delete
                                </Button>
                                <Box sx={{ flex: 1 }} />
                                {openMsg.isDocRequest && !dUploaded && (
                                    <Button onClick={() => { const mm = openMsg; setOpenMsg(null); openUpload(mm); }}
                                        startIcon={<UploadFileRoundedIcon sx={{ fontSize: 17 }} />} sx={{ ...solidBtn, height: 40, px: 2.2 }}>
                                        {dStatus === 'rejected' ? 'Re-upload document' : 'Upload document'}
                                    </Button>
                                )}
                                {openMsg.actionPath && !openMsg.isDocRequest && (
                                    <Button onClick={() => goTo(openMsg)} endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />} sx={{ ...solidBtn, height: 40, px: 2.2 }}>
                                        Open
                                    </Button>
                                )}
                                <Button onClick={() => setOpenMsg(null)} sx={{ ...ghostBtn, height: 40, px: 2 }}>Close</Button>
                            </DialogActions>
                        </>
                    );
                })()}
            </Dialog>

            <ConfirmDialog
                open={confirm}
                onClose={() => setConfirm(false)}
                onConfirm={() => { clearInbox(); setConfirm(false); }}
                title="Clear the inbox?"
                body={`All ${items.length} message(s) will be deleted. The underlying requests and documents are not affected.`}
                confirmLabel="Clear all"
            />

            <Dialog open={Boolean(upload)} onClose={() => !upload?.saving && setUpload(null)} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>Upload Document</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }}>
                        For <strong>{upload?.docName || 'this request'}</strong>. It's submitted for review once uploaded.
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    {upload?.resolving ? (
                        <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress size={22} /></Box>
                    ) : (
                        <Stack spacing={1.8}>
                            <Box>
                                <input ref={fileRef} type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                    onChange={(e) => { const f = e.target.files?.[0]; if (f) setUpload((u) => u && { ...u, file: f }); }} />
                                <Button onClick={() => fileRef.current?.click()} startIcon={<UploadFileRoundedIcon sx={{ fontSize: 18 }} />}
                                    sx={{ ...ghostBtn, height: 44, width: '100%', justifyContent: 'flex-start', px: 1.6 }}>
                                    {upload?.file ? upload.file.name : 'Choose a file (PDF, JPG, PNG, DOC · max 10 MB)'}
                                </Button>
                            </Box>
                            <TextField label="Note (optional)" size="small" fullWidth multiline minRows={2} value={upload?.note || ''}
                                onChange={(e) => setUpload((u) => u && { ...u, note: e.target.value })} placeholder="e.g. Scanned from the original"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '7px', fontSize: 14, bgcolor: '#F8FAFC' } }} />
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setUpload(null)} disabled={upload?.saving} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                    <Button onClick={submitUpload} disabled={upload?.saving || upload?.resolving || !upload?.file}
                        startIcon={upload?.saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <UploadFileRoundedIcon sx={{ fontSize: 17 }} />}
                        sx={{ ...solidBtn, height: 40, px: 2.4, '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' } }}>
                        {upload?.saving ? 'Uploading…' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={Boolean(snack)} autoHideDuration={3000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(null)} severity={snack?.sev || 'success'} variant="filled" sx={{ borderRadius: '7px' }}>{snack?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
